import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Calendar, Download } from "lucide-react";

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);

  const pollStatus = useCallback(async (sid) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (_e) {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setStatus("failed");
        } else {
          setTimeout(tick, POLL_INTERVAL_MS);
        }
      }
    };
    tick();
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatus(sessionId);
  }, [sessionId, pollStatus]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="payment-success">
      {status === "checking" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto text-[var(--red)] animate-spin mb-6" />
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Confirming your payment…</h1>
          <p className="text-[var(--text-2)]">This usually takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-6" />
          <h1 className="font-display text-4xl font-semibold tracking-tight mb-3" data-testid="payment-confirmed">Payment confirmed</h1>
          <p className="text-[var(--text-2)] mb-2">
            Total: <span className="text-white">${((details?.amount_total || 0) / 100).toFixed(2)} {details?.currency?.toUpperCase()}</span>
          </p>
          <p className="text-[var(--text-2)] mb-8">A receipt has been sent to your email.</p>
          {details?.metadata?.kind === "booking" && details?.metadata?.booking_id && (
            <BookingCalendarActions bookingId={details.metadata.booking_id} />
          )}
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/portal" className="btn-red">Go to Portal</Link>
            <Link to="/" className="btn-ghost">Home</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle className="w-12 h-12 mx-auto text-[var(--red)] mb-6" />
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-3">
            {status === "timeout" ? "Still processing" : "Payment not confirmed"}
          </h1>
          <p className="text-[var(--text-2)] mb-8">
            {status === "timeout"
              ? "Check your email for the receipt — confirmation may still arrive shortly."
              : "If you completed checkout, please contact us and we'll verify."}
          </p>
          <Link to="/contact" className="btn-red">Contact Support</Link>
        </>
      )}
    </div>
  );
}

function BookingCalendarActions({ bookingId }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const icsUrl = `${apiBase}/api/bookings/${bookingId}/calendar.ics`;
  const openGoogle = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/google-calendar`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (_e) {}
  };
  return (
    <div className="mt-2" data-testid="payment-calendar-actions">
      <p className="overline mb-3 text-[var(--silver)]">Add your consultation to your calendar</p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button onClick={openGoogle} className="btn-red" data-testid="add-to-google-calendar">
          <Calendar className="w-4 h-4" /> Add to Google Calendar
        </button>
        <a href={icsUrl} className="btn-ghost" data-testid="download-ics" download>
          <Download className="w-4 h-4" /> Download .ics
        </a>
      </div>
      <p className="text-xs text-[var(--text-3)] mt-3">The .ics file is also attached to your confirmation email.</p>
    </div>
  );
}
